import * as React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { selectors } from '@src/shared/src';
import { dismissNotification, notify } from '@toolkit/util/app';
// Constants
import { NOTIF_TYPE } from '@src/shared/src/const/app';
// Interfaces
import { IRootState } from '@src/store';

const TOAST_ID = 'flexlane-warning';
// An id of its OWN rather than the one above: the backend only lets both sentences through when they are
// about DIFFERENT fares, so they have to be able to sit on screen at once.
const CONTRACT_TOAST_ID = 'flexlane-contract';

// A FlexLane (Koala CFAR) cover the backend could not attach leaves the booking intact but uncovered
// (lanes-planes/backend#15085 for the quote step, #15086 for the contract step).
//
// Every sentence here is rendered VERBATIM, with no t() and no Locize key: the backend sends them finished
// and in the traveller's own language. A key on screen instead of a sentence means the bug is in the
// backend's locale files.
export const ShowFlexLaneWarning:React.FC = () => {
  const basket = useSelector((state:IRootState) => selectors.checkout.basket(state.checkout));
  const warning = basket?.flexlaneWarning;
  const contractMessage = basket?.flexlaneContractMessage;

  React.useEffect(() => {
    // NOTIF_TYPE.WARNING sets timeout: false, so the toast never closes by itself: a re-prepare whose quote
    // succeeds has to take it down explicitly, or the traveller goes on reading that the cover is missing
    // while it exists and the organisation is being billed for it. displayMode 2 is 'replace', keyed on
    // TOAST_ID, so a second failure worded differently lands in the first one's place rather than beside it.
    if (warning) {
      notify(warning, NOTIF_TYPE.WARNING, [], 2, TOAST_ID);
    } else {
      dismissNotification(TOAST_ID);
    }
  }, [warning]);

  // The backend broadcasts the basket again when FlexLane::ContractJob finishes, which is what settles this
  // on the confirmation screen: the failure sentence arrives, or the message clears once the cover landed.
  // A contract still being created sends a status and NO message on purpose - the confirm loader the status
  // holds up is the waiting state, and a toast beside it would say the same thing twice.
  React.useEffect(() => {
    if (contractMessage) {
      notify(contractMessage, NOTIF_TYPE.WARNING, [], 2, CONTRACT_TOAST_ID);
    } else {
      dismissNotification(CONTRACT_TOAST_ID);
    }
  }, [contractMessage]);

  // The sentences say "this booking", so the toasts cannot outlive CheckoutConn. Its own effect rather than
  // a cleanup on the two above, which would dismiss and re-show on every reworded message.
  React.useEffect(() => () => {
    dismissNotification(TOAST_ID);
    dismissNotification(CONTRACT_TOAST_ID);
  }, []);

  return null;
};
